/**
 * GridsContext - Contexto compartido para gestión de mallas H3
 *
 * @version 1.0
 * @updated 2026-02-19
 * @module core/spatial
 *
 * @description
 * Contexto global que sincroniza el estado de mallas visibles entre:
 * - GridsPanel (sidebar izquierdo)
 * - GridsDropdown (MapControls, arriba a la derecha)
 *
 * @remarks
 * Ambos componentes pueden alternar la visibilidad de mallas y el estado
 * se sincroniza automáticamente a través de este contexto.
 */

import { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import type { SpatialGrid } from './types';

/**
 * Tipo de fila de celda
 * @remarks
 * Duplicado desde CellsSection para evitar dependencia circular
 */
export interface CellRow {
  h3Index: string;
  cellCode: string | null;
  cellName: string | null;
  env: Record<string, number>;
}

/**
 * Posición del popover de celda en el viewport
 */
export interface CellPopoverPosition {
  x: number;
  y: number;
}

/**
 * Estado del contexto
 */
export interface GridsContextState {
  /** Mallas disponibles en el proyecto actual */
  grids: SpatialGrid[];
  /** IDs de las mallas actualmente visibles en el mapa */
  visibleGrids: Set<string>;
  /** ID de la malla activa (para panel de celdas en RightPanel) */
  activeGridId: string | null;
  /** Indica si la malla activa es interactiva (responde a clicks en celdas) */
  isGridInteractive: boolean;
  /** Filas de celdas de la malla activa */
  cellRows: CellRow[];
  /** H3 index de la celda actualmente en hover */
  hoveredCellId: string | null;
  /** H3 index de la celda seleccionada */
  selectedCellId: string | null;
  /** Posición del popover de celda en el viewport (null si no está visible) */
  cellPopoverPosition: CellPopoverPosition | null;
}

/**
 * Acciones del contexto
 */
export interface GridsContextActions {
  /** Establece la lista de mallas disponibles */
  setGrids: (grids: SpatialGrid[] | ((prev: SpatialGrid[]) => SpatialGrid[])) => void;
  /** Alterna la visibilidad de una malla en el mapa */
  toggleGridVisibility: (gridId: string) => void;
  /** Marca múltiples mallas como visibles */
  setVisibleGrids: (gridIds: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  /** Establece la malla activa (para panel de celdas) */
  setActiveGridId: (gridId: string | null) => void;
  /** Activa/desactiva la interactividad de la malla activa */
  setIsGridInteractive: (interactive: boolean) => void;
  /** Establece las filas de celdas */
  setCellRows: (rows: CellRow[] | ((prev: CellRow[]) => CellRow[])) => void;
  /** Establece la celda en hover */
  setHoveredCellId: (id: string | null) => void;
  /** Establece la celda seleccionada */
  setSelectedCellId: (id: string | null) => void;
  /** Establece la posición del popover de celda */
  setCellPopoverPosition: (position: CellPopoverPosition | null) => void;
}

/**
 * Tipo completo del contexto
 */
export type GridsContextType = GridsContextState & GridsContextActions;

/**
 * Contexto
 */
const GridsContext = createContext<GridsContextType | undefined>(undefined);

/**
 * Props del provider
 */
export interface GridsProviderProps {
  children: ReactNode;
}

/**
 * Provider del contexto
 */
export function GridsProvider({ children }: GridsProviderProps): JSX.Element {
  const [grids, setGridsState] = useState<SpatialGrid[]>([]);
  const [visibleGrids, setVisibleGrids] = useState<Set<string>>(new Set());
  const [activeGridId, setActiveGridId] = useState<string | null>(null);
  const [isGridInteractive, setIsGridInteractive] = useState<boolean>(false);
  const [cellRows, setCellRows] = useState<CellRow[]>([]);
  const [hoveredCellId, setHoveredCellId] = useState<string | null>(null);
  const [selectedCellId, setSelectedCellId] = useState<string | null>(null);
  const [cellPopoverPosition, setCellPopoverPosition] = useState<CellPopoverPosition | null>(null);

  /**
   * Wrapper de setGrids - NO auto-activa mallas
   * La visualización debe manejarse en el componente que consume el contexto
   *
   * IMPORTANTE: No tiene dependencias para evitar loops infinitos.
   * Usa el callback form de setState para acceder al valor previo.
   */
  const setGrids = useCallback((newGrids: SpatialGrid[] | ((prev: SpatialGrid[]) => SpatialGrid[])): void => {
    // Usar callback form para evitar dependencia en 'grids'
    setGridsState(prev => {
      const gridsArray = typeof newGrids === 'function' ? newGrids(prev) : newGrids;
      return gridsArray;
    });
  }, []); // Sin dependencias - estable

  /**
   * Alterna la visibilidad de una malla en el mapa.
   * Si está visible, la oculta; si está oculta, la muestra.
   */
  const toggleGridVisibility = useCallback((gridId: string): void => {
    setVisibleGrids(prev => {
      const next = new Set(prev);
      if (next.has(gridId)) {
        next.delete(gridId);
      } else {
        next.add(gridId);
      }
      return next;
    });
  }, []);

  const value: GridsContextType = {
    // Estado
    grids,
    visibleGrids,
    activeGridId,
    isGridInteractive,
    cellRows,
    hoveredCellId,
    selectedCellId,
    cellPopoverPosition,
    // Acciones
    setGrids,
    toggleGridVisibility,
    setVisibleGrids,
    setActiveGridId,
    setIsGridInteractive,
    setCellRows,
    setHoveredCellId,
    setSelectedCellId,
    setCellPopoverPosition,
  };

  return <GridsContext.Provider value={value}>{children}</GridsContext.Provider>;
}

/**
 * Hook para usar el contexto
 */
export function useGrids(): GridsContextType {
  const context = useContext(GridsContext);
  if (!context) {
    throw new Error('useGrids must be used within GridsProvider');
  }
  return context;
}

/**
 * Hook seguro que retorna null si no hay GridsProvider.
 * Útil para componentes que pueden renderizarse fuera de GridsProvider (ej: ProjectForm).
 */
export function useGridsSafe(): GridsContextType | null {
  return useContext(GridsContext) ?? null;
}
